import React, { useState, useEffect, useRef } from 'react'
import AsyncCreatableSelect from 'react-select/async-creatable'
import { showSuccess, showError } from '../utils/notifications.js'
import ImportFundAssetsModal from './ImportFundAssetsModal.jsx'
import { t } from '../../i18n.js'

// Single fund asset select field.
// `translations` is the new_valuation.fund_asset_select subtree from Rails.
function FundAssetField({ fundId, value, onChange, onRemove, searchUrl, createUrl, csrfToken, canRemove, fieldKey, excludeIds, onAssetCreated, selectRef, translations }) {
  const [isLoading, setIsLoading] = useState(false)

  const loadOptions = async (inputValue) => {
    if (!fundId) return []
    
    const response = await fetch(`${searchUrl}?fund_id=${fundId}&q=${encodeURIComponent(inputValue)}`)
    const data = await response.json()
    
    // Filter out already selected assets (except current value)
    const filtered = data.filter(item => 
      !excludeIds.includes(item.id) || (value && item.id === value.value)
    )
    
    return filtered.map(item => ({ value: item.id, label: item.title }))
  }

  const handleCreate = async (inputValue) => {
    setIsLoading(true)
    try {
      const response = await fetch(createUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify({ fund_asset: { title: inputValue, fund_id: fundId } })
      })
      
      const data = await response.json()
      if (data.success) {
        const newOption = { value: data.id, label: data.title }
        onChange(newOption)
        showSuccess(t('created', translations, { title: data.title }))

        // Notify parent to refresh all fields
        if (onAssetCreated) {
          onAssetCreated(newOption)
        }

        return newOption
      } else {
        showError(data.errors ? Object.values(data.errors).flat().join(', ') : t('failure', translations))
      }
    } catch (error) {
      showError(t('failure', translations))
      console.error('Error creating fund asset:', error)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="mb-2">
      <div className="input-group">
              <div style={{ flex: 1 }}>
                <AsyncCreatableSelect
                  ref={selectRef}
                  key={fieldKey}
                  cacheOptions
                  defaultOptions
                  loadOptions={loadOptions}
                  value={value}
                  onChange={onChange}
                  onCreateOption={handleCreate}
                  placeholder={fundId ? t('placeholder', translations) : t('placeholder_disabled', translations)}
                  isClearable
                  isDisabled={!fundId || isLoading}
                  isLoading={isLoading}
                  formatCreateLabel={(inputValue) => t('create_label', translations, { input: inputValue })}
                  className="react-select-container"
                  classNamePrefix="react-select"
                />
              </div>
        {canRemove && (
          <button
            type="button"
            className="btn btn-outline-danger"
            onClick={onRemove}
          >
            <i className="bi bi-trash"></i>
          </button>
        )}
      </div>
    </div>
  )
}

// Multiple fund asset selection with dynamic add/remove.
// `translations` is the new_valuation.fund_asset_select subtree from Rails.
export default function FundAssetSelect({ fundId, values, onChange, searchUrl, createUrl, bulkCreateUrl, disabled, csrfToken, hideImport = false, translations }) {
  const [fields, setFields] = useState([{ id: 0, value: null }])
  const [nextId, setNextId] = useState(1)
  const [refreshKey, setRefreshKey] = useState(0)
  const [showImportModal, setShowImportModal] = useState(false)
  const [pastedText, setPastedText] = useState('') // Store pasted text for auto-import
  const containerRef = useRef(null) // Reference to container for paste detection
  const selectRefs = useRef({}) // Store refs to react-select instances

  // Reset fields when fund changes
  useEffect(() => {
    setFields([{ id: 0, value: null }])
    setNextId(1)
    setRefreshKey(prev => prev + 1)
  }, [fundId])
  
  // Listen for paste events in the container
  useEffect(() => {
    const handlePaste = (e) => {
      if (!fundId) return // Don't handle paste if no fund selected
      
      const pastedData = e.clipboardData?.getData('text')
      
      if (pastedData && pastedData.includes('\n')) {
        // Multi-line paste detected
        const lines = pastedData.split('\n').map(line => line.trim()).filter(line => line.length > 0)
        
        if (lines.length > 1) {
          e.preventDefault() // Prevent default paste
          e.stopPropagation()
          
          // Close all dropdowns and trigger bulk import
          closeAllDropdowns()
          setPastedText(pastedData)
          setShowImportModal(true)
        }
      }
    }
    
    const container = containerRef.current
    if (container) {
      container.addEventListener('paste', handlePaste, true) // Use capture phase
      
      return () => {
        container.removeEventListener('paste', handlePaste, true)
      }
    }
  }, [fundId])

  const addField = () => {
    setFields([...fields, { id: nextId, value: null }])
    setNextId(nextId + 1)
  }

  const removeField = (id) => {
    if (fields.length === 1) return // Keep at least one field
    const newFields = fields.filter(field => field.id !== id)
    setFields(newFields)
    
    // Update parent component with remaining selected values
    const selectedValues = newFields
      .map(field => field.value)
      .filter(v => v !== null)
    onChange(selectedValues)
  }

  const updateField = (id, value) => {
    setFields(fields.map(field => 
      field.id === id ? { ...field, value } : field
    ))
    
    // Update parent component with all selected values
    const selectedValues = fields.map(field => 
      field.id === id ? value : field.value
    ).filter(v => v !== null)
    
    onChange(selectedValues)
  }
  
  const handleAssetCreated = (newOption) => {
    // Force refresh of all fields to show the new asset
    setRefreshKey(prev => prev + 1)
  }
  
  const handleImportComplete = (createdAssets, existingAssets) => {
    // Force refresh of all fields to show imported assets
    setRefreshKey(prev => prev + 1)
    
    // Combine created and existing assets
    const allAssets = [...createdAssets, ...existingAssets]
    
    // Create new fields for all imported assets
    const newFields = allAssets.map((asset, index) => ({
      id: nextId + index,
      value: { value: asset.id, label: asset.title }
    }))
    
    setFields(newFields)
    setNextId(nextId + allAssets.length)
    
    // Update parent with selected values
    onChange(newFields.map(f => f.value))
    
    // Clear pasted text
    setPastedText('')
  }
  
  const handleCloseModal = () => {
    setShowImportModal(false)
    setPastedText('')
  }
  
  // Function to close all react-select dropdowns
  const closeAllDropdowns = () => {
    Object.values(selectRefs.current).forEach(selectRef => {
      if (selectRef && selectRef.blur) {
        selectRef.blur()
      }
    })
  }
  
  // Get IDs of selected assets to exclude from other fields
  const getExcludedIds = () => {
    return fields
      .map(field => field.value?.value)
      .filter(id => id !== undefined && id !== null)
  }

  return (
    <div className="mb-3" ref={containerRef}>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <label className="form-label mb-0">{t('label', translations)}</label>
        <div className="d-flex gap-2">
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            onClick={addField}
            disabled={disabled}
          >
            <i className="bi bi-plus-circle me-1"></i>
            {t('add_asset', translations)}
          </button>
          {!hideImport && (
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => {
                closeAllDropdowns()
                setShowImportModal(true)
              }}
              disabled={disabled}
            >
              <i className="bi bi-upload me-1"></i>
              {t('import', translations)}
            </button>
          )}
        </div>
      </div>
      
      {fields.map(field => (
        <FundAssetField
          key={field.id}
          fieldKey={`${refreshKey}-${field.id}`}
          fundId={fundId}
          value={field.value}
          onChange={(value) => updateField(field.id, value)}
          onRemove={() => removeField(field.id)}
          searchUrl={searchUrl}
          createUrl={createUrl}
          csrfToken={csrfToken}
          canRemove={fields.length > 1}
          excludeIds={getExcludedIds()}
          onAssetCreated={handleAssetCreated}
          selectRef={(ref) => {
            if (ref) {
              selectRefs.current[field.id] = ref
            } else {
              delete selectRefs.current[field.id]
            }
          }}
          translations={translations}
        />
      ))}

      <div className="form-text">
        {disabled
          ? t('help_disabled', translations)
          : t('help', translations)}
      </div>
      
      <ImportFundAssetsModal
        fundId={fundId}
        isOpen={showImportModal}
        onClose={handleCloseModal}
        onImportComplete={handleImportComplete}
        bulkCreateUrl={bulkCreateUrl}
        searchUrl={searchUrl}
        csrfToken={csrfToken}
        initialText={pastedText}
      />
    </div>
  )
}

